import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient, api, getApiErrorMessage } from '../../api/client';
import { fmtCurrency, sumMoney } from '../../lib/display-labels';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import type { ApiResponse } from '../../types/api-manifest';
import type { FleetExpenseCreateResponse } from '../../types/fleet-expense';
import {
  ArrowLeft, Plus, Camera, Upload, PenLine, Image, ChevronDown, Loader2, FileText,
} from 'lucide-react';

/**
 * ──────────────────────────────────────────────────────────────────────────
 *  THIS SCREEN USED TO OPEN ON AN INVENTED RECEIPT.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * A module-level `mockExtracted` — vendor "Nisku Truck Centre", invoice
 * NTC-2026-4418, four line items, $434.70 — was rendered on FIRST PAINT under
 * a green "AI extracted" badge, with a **Save expense — $434.70** button under
 * it. Nothing had been uploaded. The badge is the worst part: it does not say
 * "example", it says the system read a document, and the figures carried the
 * precision of something read off paper. A user who trusted it would have
 * written a $434.70 expense against a vendor they never bought from.
 *
 * The cost summary beside it was literal too (YTD $8,420, $0.024/km, all-time
 * $24,180 over 341,220 km), as was the unit it assigned to — a hardcoded
 * `T-007` Kenworth. Embedded in the Fleet screen this component receives NO
 * route param at all, so that unit was invented for a tab that is not even
 * scoped to a unit.
 *
 * ── WHAT IS UNDERNEATH: A REAL PATH, IN TWO PIECES ────────────────────────
 *
 * Extraction: `POST /asset-expenses/ocr` is a genuine Claude Vision read
 * (`asset-expenses.routes.ts`). It is also STATELESS and asset-agnostic — the
 * handler reads only the uploaded file, persists nothing, and never looks at
 * an equipment id — so using it to read a truck's receipt is what it does, not
 * a borrowed endpoint.
 *
 * **The file field is named `receipt`.** Both pre-existing call sites got this
 * wrong (`file` in components/equipment/AssetExpenses.tsx, `image` in
 * components/maintenance/MaintenanceRecordDialog.tsx) and so every upload from
 * either 400s with "receipt file is required". Both are corrected in this PR;
 * the OCR feature has never worked from the UI.
 *
 * Saving: `POST /trucking/fleet-expenses`. Note this family takes **camelCase**
 * on the write — `expenseDate`, `fleetId` — which is the exception to the
 * snake_case-on-writes rule, not the rule. Required: expenseDate, amount,
 * category.
 *
 * ── WHAT HAS NO SOURCE, AND SO IS NOT SHOWN ───────────────────────────────
 *
 * - **Cost per km / per mile.** No odometer reaches this table and no endpoint
 *   returns the ratio. It renders '—'.
 * - **A vendor and invoice-ref column.** `trucking_fleet_expenses` has neither,
 *   so the OCR's vendor and invoice ref are folded into `description` on save
 *   rather than silently dropped after being shown.
 * - **An odometer field.** The insert accepts no such column; an input for it
 *   would have discarded what was typed.
 * - **Any per-unit summary endpoint.** The totals below are tallied from the
 *   rows on screen, which is honest only because this list is unpaginated —
 *   the route has no LIMIT and returns every matching row. Labelled "logged",
 *   not "YTD", because the query is not date-bounded either.
 */

interface LineItem { description: string; amount: number }

interface ExtractedReceipt {
  vendor: string;
  date: string;
  invoiceRef: string;
  lineItems: LineItem[];
  total: number | null;
}

interface Expense {
  id: string;
  date: string;
  description: string;
  amount: number | null;
  category: string;
  fleetUnitNumber: string;
}

interface FleetUnit {
  id: string;
  unitNumber: string;
  label: string;
}

const CATEGORIES = ['Maintenance', 'Fuel', 'Repair', 'Tires', 'Registration', 'Insurance', 'Other'];

/**
 * Exhaustive over the categories this screen saves (lowercased, as the route
 * stores them). Seven categories, five hues: grouped by meaning, the label tells
 * them apart. `category` is free text on the table, so anything else is a slate
 * dash, never a guessed category.
 */
const CATEGORY: Record<string, { label: string; fill: string }> = {
  maintenance: { label: 'Maintenance', fill: 'bg-fi-signal-amber' },
  repair: { label: 'Repair', fill: 'bg-fi-signal-red' },
  tires: { label: 'Tires', fill: 'bg-fi-signal-amber' },
  fuel: { label: 'Fuel', fill: 'bg-fi-signal-blue' },
  registration: { label: 'Registration', fill: 'bg-fi-signal-green' },
  insurance: { label: 'Insurance', fill: 'bg-fi-signal-green' },
  other: { label: 'Other', fill: 'bg-fi-signal-slate' },
};
const categoryOf = (c: string) =>
  Object.prototype.hasOwnProperty.call(CATEGORY, c) ? CATEGORY[c] : { label: '—', fill: 'bg-fi-signal-slate' };

const inputClass = 'w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';

export default function FleetExpensesScreen({ embedded }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const { unitId } = useParams();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  /* ---- entry state. 'idle' is the only first-paint state: nothing is shown
         until a receipt has actually been read, or manual entry chosen. ---- */
  const [mode, setMode] = useState<'idle' | 'ocr' | 'manual'>('idle');
  const [scanning, setScanning] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedReceipt | null>(null);
  const [category, setCategory] = useState('Maintenance');
  const [notes, setNotes] = useState('');
  const [assignedUnitId, setAssignedUnitId] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualVendor, setManualVendor] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  // The expense date defaults to the TENANT's today (companies.time_zone), not
  // the device's. '' only seeds the input when no zone is known, and the save
  // below refuses an empty date rather than sending it.
  const timeZone = useTenantTimeZone();
  const [manualDate, setManualDate] = useState(todayCalendarDate(timeZone) ?? '');

  const expensesQuery = useQuery({
    queryKey: ['trucking-fleet-expenses', unitId],
    queryFn: () => apiClient.get('/trucking/fleet-expenses', { params: unitId ? { fleet_id: unitId } : {} }).then(r => {
      const items = r.data?.data ?? r.data ?? [];
      return items.map((e: any): Expense => ({
        id: e.id,
        date: e.expense_date || e.expenseDate || '',
        description: e.description || '',
        // An absent amount stays absent. `Number(null)` is 0, and a $0.00
        // expense is a claim nobody made.
        amount: e.amount === null || e.amount === undefined || e.amount === '' ? null : Number(e.amount),
        category: String(e.category || 'other').toLowerCase(),
        fleetUnitNumber: e.fleet_unit_number || '',
      }));
    }),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const expensesFailed = queryLoadFailure(expensesQuery) != null;
  const apiExpenses = expensesFailed ? undefined : expensesQuery.data;

  // No fallback. Every tenant with no expense rows — which is all of them —
  // was shown five fabricated ones (Nisku Truck Centre, a $1,840 brake drum)
  // as their own history.
  const expenses: Expense[] = useMemo(() => apiExpenses ?? [], [apiExpenses]);

  /* ---- fleet units, for the assignment picker. The unit a receipt belongs to
         is a choice the user makes; it was previously a hardcoded T-007. ---- */
  const { data: fleetUnits = [] } = useQuery({
    queryKey: ['trucking-fleet', 'expense-picker'],
    queryFn: () => apiClient.get<ApiResponse<'GET /api/v1/trucking/fleet'>>('/trucking/fleet').then(r => {
      const items = r.data?.data ?? r.data ?? [];
      return items.map((f: any): FleetUnit => {
        const unitNumber = f.unit_number || f.unitNumber || '';
        const spec = [f.year, f.make, f.model].filter(Boolean).join(' ');
        return {
          id: f.id,
          unitNumber,
          label: spec ? `${unitNumber || f.id} — ${spec}` : (unitNumber || f.id),
        };
      });
    }),
  });

  /* ---- totals. Tallied from the rows above; see the docblock. ---- */
  // All-or-nothing: one row without an amount and the total is a dash, not a
  // smaller figure. (`amount` is NOT NULL on the table, so this is a guard.)
  const { loggedTotal, byCategory } = useMemo(() => {
    const groups: Record<string, (number | null)[]> = {};
    for (const e of expenses) (groups[e.category] ??= []).push(e.amount);
    const cats: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(groups)) cats[k] = sumMoney(v);
    return { loggedTotal: expenses.length ? sumMoney(expenses.map(e => e.amount)) : null, byCategory: cats };
  }, [expenses]);

  const topCategories = Object.entries(byCategory)
    .filter((entry): entry is [string, number] => entry[1] != null)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['trucking-fleet-expenses'] });

  const resetForm = () => {
    setMode('idle');
    setExtracted(null);
    setCategory('Maintenance');
    setNotes('');
    setAssignedUnitId('');
    setManualAmount('');
    setManualVendor('');
    setManualDescription('');
    setManualDate(todayCalendarDate(timeZone) ?? '');
  };

  const createMutation = useMutation({
    // camelCase body — this route reads `expenseDate`/`fleetId`, not the
    // snake_case every other write here sends. Verified against
    // trucking-fleet-expenses.routes.ts, not assumed.
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.post<FleetExpenseCreateResponse>('/trucking/fleet-expenses', body),
    onSuccess: () => {
      invalidate();
      resetForm();
      toast.success('Expense saved');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to save expense')),
  });

  /* ---- OCR ---- */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      // The route reads body['receipt']. Any other key 400s.
      fd.append('receipt', file);
      // Do NOT set Content-Type — the client.ts interceptor strips it so the
      // browser sets the multipart boundary automatically.
      const res = await api.assetExpenses.ocr(fd);
      const d = res.data?.data ?? res.data ?? {};
      const total = d.total === null || d.total === undefined ? null : Number(d.total);
      setExtracted({
        vendor: d.vendor ?? '',
        date: d.date ?? '',
        invoiceRef: d.invoice_ref ?? d.invoiceRef ?? '',
        lineItems: (d.line_items ?? d.lineItems ?? []).map((li: any) => ({
          description: li.description ?? '',
          amount: Number(li.amount ?? 0),
        })),
        total: total !== null && Number.isFinite(total) ? total : null,
      });
      // `category_suggestion` is the key the route returns; `category` is not a
      // field it sends. A suggestion only pre-selects the dropdown — the user
      // confirms it before anything is written.
      const suggested = String(d.category_suggestion ?? '');
      const matched = CATEGORIES.find(c => c.toLowerCase() === suggested.toLowerCase());
      setCategory(matched ?? 'Maintenance');
      setMode('ocr');
    } catch (err) {
      // The panel stays closed on a failure. A half-read receipt shown as a
      // saveable one is the bug this screen is being fixed for.
      setExtracted(null);
      setMode('idle');
      toast.error(getApiErrorMessage(err, 'Could not read that receipt — enter the expense manually.'));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* ---- saves ---- */
  // trucking_fleet_expenses has no vendor or invoice_ref column. Folding them
  // into the description keeps what the scan read attached to the row instead
  // of showing it and then discarding it on save.
  const composeDescription = (vendor: string, body: string, invoiceRef: string) =>
    [vendor, body, invoiceRef ? `Ref ${invoiceRef}` : ''].filter(Boolean).join(' — ') || null;

  const handleSaveOcr = () => {
    if (!extracted) return;
    if (extracted.total === null) {
      toast.error('No total was read from this receipt — enter the expense manually.');
      return;
    }
    if (!extracted.date) {
      toast.error('No date was read from this receipt — enter the expense manually.');
      return;
    }
    createMutation.mutate({
      fleetId: assignedUnitId || null,
      expenseDate: extracted.date,
      amount: extracted.total,
      category: category.toLowerCase(),
      description: composeDescription(
        extracted.vendor,
        [extracted.lineItems.map(li => li.description).filter(Boolean).join('; '), notes]
          .filter(Boolean).join(' · '),
        extracted.invoiceRef,
      ),
    });
  };

  const handleSaveManual = () => {
    const amt = parseFloat(manualAmount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!manualDate) { toast.error('Enter a date'); return; }
    createMutation.mutate({
      fleetId: assignedUnitId || null,
      expenseDate: manualDate,
      amount: amt,
      category: category.toLowerCase(),
      description: composeDescription(
        manualVendor,
        [manualDescription, notes].filter(Boolean).join(' · '),
        '',
      ),
    });
  };

  const unitPicker = (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Assign to Unit</p>
      <div className="relative">
        <select
          aria-label="Assign to unit"
          value={assignedUnitId}
          onChange={e => setAssignedUnitId(e.target.value)}
          className={inputClass + ' appearance-none pr-8'}
        >
          <option value="">Not assigned to a unit</option>
          {fleetUnits.map((u: FleetUnit) => <option key={u.id} value={u.id}>{u.label}</option>)}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fieldiq-muted pointer-events-none" />
      </div>
    </div>
  );

  const categoryPicker = (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Category</p>
      <div className="relative">
        <select aria-label="Category" value={category} onChange={e => setCategory(e.target.value)} className={inputClass + ' appearance-none pr-8'}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fieldiq-muted pointer-events-none" />
      </div>
    </div>
  );

  const notesField = (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Notes</p>
      <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" className={inputClass} />
    </div>
  );

  return (
    <div data-testid="fleet-expenses" className={embedded ? 'text-fieldiq-text' : 'min-h-full bg-fieldiq-dark text-fieldiq-text pb-[var(--bottom-chrome)]'}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        aria-label="Upload receipt or invoice"
        onChange={handleFileChange}
      />

      {!embedded && (
        <div className="border-b border-border px-6 py-4">
          <button type="button" aria-label="Back" onClick={() => navigate(-1)} className="flex items-center gap-2 rounded px-2 py-1 text-lg font-semibold text-fieldiq-text hover:bg-fieldiq-card">
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center justify-between mt-2">
            <div>
              <h1 className="text-base font-bold text-foreground">Fleet Expenses</h1>
              {unitId && <p className="text-xs text-fieldiq-muted">{unitId}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { resetForm(); setMode('manual'); }}
                className="fi-action fi-action--compact fi-action--primary"
              >
                <Plus size={14} /> Log expense
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-5 mt-4'}>
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left — Capture & Entry */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* Capture zone */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-2">Capture Receipt or Invoice</p>
              <div className="bg-fieldiq-card border-2 border-dashed border-fieldiq-border rounded-fi-panel p-6 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-fi-control bg-fieldiq-dark flex items-center justify-center">
                  {scanning
                    ? <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
                    : <Image className="w-6 h-6 text-fieldiq-muted" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-fieldiq-text">
                    {scanning ? 'Reading receipt…' : 'Scan or upload receipt / invoice'}
                  </p>
                  <p className="text-[11px] text-fieldiq-muted">
                    Vendor, amount, date and line items are read from the document you upload.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    disabled={scanning}
                    onClick={() => fileRef.current?.click()}
                    className="fi-action fi-action--compact fi-action--primary disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" /> Take photo
                  </button>
                  <button
                    disabled={scanning}
                    onClick={() => fileRef.current?.click()}
                    className="fi-action fi-action--compact fi-action--primary disabled:opacity-50"
                  >
                    <Upload className="w-3.5 h-3.5" /> Upload file
                  </button>
                  <button
                    disabled={scanning}
                    onClick={() => { resetForm(); setMode('manual'); }}
                    className="px-3 py-1.5 text-xs font-semibold border border-fieldiq-border rounded-fi-control text-fieldiq-text flex items-center gap-1.5 hover:bg-fieldiq-card disabled:opacity-50"
                  >
                    <PenLine className="w-3.5 h-3.5" /> Enter manually
                  </button>
                </div>
              </div>
            </div>

            {/* Extracted receipt — rendered ONLY after a real extraction */}
            {mode === 'ocr' && extracted && (
              <div className="space-y-4">
                <div className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="rounded-fi-pill border border-black/20 bg-fi-signal-green px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink">Read from your receipt</span>
                    <span className="text-[11px] text-fieldiq-muted">Check every figure before saving</span>
                  </div>
                  <div className="bg-fieldiq-dark border border-fieldiq-border rounded-fi-panel p-4 space-y-1.5">
                    <div className="flex justify-between text-xs text-fieldiq-muted mb-2">
                      <span>Vendor</span>
                      <span className="font-semibold text-fieldiq-text">{extracted.vendor || '—'}</span>
                    </div>
                    {extracted.lineItems.map((li, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-fieldiq-muted">{li.description || '—'}</span>
                        <span className="font-medium text-fieldiq-text">{fmtCurrency(li.amount)}</span>
                      </div>
                    ))}
                    <div className="border-t border-fieldiq-border pt-2 mt-2 flex justify-between text-sm font-bold">
                      <span className="text-fieldiq-text">Total</span>
                      <span className="text-fieldiq-text">{fmtCurrency(extracted.total)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {[
                      { l: 'VENDOR', v: extracted.vendor },
                      { l: 'DATE', v: formatCalendarDate(extracted.date, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '' },
                      { l: 'INVOICE REF', v: extracted.invoiceRef },
                    ].map(s => (
                      <div key={s.l} className="bg-fieldiq-dark border border-fieldiq-border rounded-fi-panel p-2.5">
                        <p className="text-[10px] text-fieldiq-muted font-semibold">{s.l}</p>
                        <p className="text-xs font-bold text-fieldiq-text">{s.v || '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {unitPicker}
                {categoryPicker}
                {notesField}

                <div className="space-y-2 pt-1">
                  <button
                    disabled={createMutation.isPending}
                    onClick={handleSaveOcr}
                    className="fi-action fi-action--standard fi-action--primary w-full justify-center disabled:opacity-50"
                  >
                    {createMutation.isPending
                      ? 'Saving…'
                      : `Save expense — ${fmtCurrency(extracted.total)}`}
                  </button>
                  <button onClick={resetForm} className="w-full py-2.5 text-sm font-semibold border border-fieldiq-border rounded-fi-control hover:bg-fieldiq-card text-fieldiq-text">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Manual entry */}
            {mode === 'manual' && (
              <div className="space-y-4">
                <div className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-4 space-y-3">
                  <p className="text-xs font-semibold text-fieldiq-text">Manual Expense Entry</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Amount ($)</p>
                      <input type="number" step="0.01" min="0" value={manualAmount} onChange={e => setManualAmount(e.target.value)} placeholder="0.00" className={inputClass} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Date</p>
                      <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Vendor</p>
                    <input value={manualVendor} onChange={e => setManualVendor(e.target.value)} placeholder="Optional" className={inputClass} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-1.5">Description</p>
                    <input value={manualDescription} onChange={e => setManualDescription(e.target.value)} placeholder="Optional" className={inputClass} />
                  </div>
                  {unitPicker}
                  {categoryPicker}
                  {notesField}
                </div>

                <div className="space-y-2 pt-1">
                  <button
                    disabled={createMutation.isPending}
                    onClick={handleSaveManual}
                    className="fi-action fi-action--standard fi-action--primary w-full justify-center disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Saving…' : 'Save expense'}
                  </button>
                  <button onClick={resetForm} className="w-full py-2.5 text-sm font-semibold border border-fieldiq-border rounded-fi-control hover:bg-fieldiq-card text-fieldiq-text">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right — Cost Summary */}
          <div className="lg:w-[380px] shrink-0 space-y-4">
            <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold">Cost Summary</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-3">
                <p className="text-[10px] text-fieldiq-muted font-semibold">LOGGED COSTS</p>
                <p className="text-xl font-bold text-fieldiq-text">{fmtCurrency(loggedTotal)}</p>
                <p className="text-[10px] text-fieldiq-muted">
                  {expenses.length === 1 ? '1 expense' : `${expenses.length} expenses`}
                </p>
              </div>
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-3">
                <p className="text-[10px] text-fieldiq-muted font-semibold">COST / KM</p>
                {/* No odometer reaches this table and no endpoint returns the
                    ratio. It read $0.024 for every tenant. */}
                <p className="text-xl font-bold text-fieldiq-text">—</p>
                <p className="text-[10px] text-fieldiq-muted">No distance recorded</p>
              </div>
            </div>

            {topCategories.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {topCategories.map(([cat, val]) => (
                  <div key={cat} className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-3 text-center">
                    <p className="text-[10px] font-semibold text-fieldiq-muted">{categoryOf(cat).label}</p>
                    <p className="text-lg font-bold text-fieldiq-text">{fmtCurrency(val)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Recent expenses */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-fieldiq-muted font-semibold mb-3">
                {expenses.length > 0 ? 'Recent Expenses' : 'Expenses'}
              </p>
              <div className="space-y-2">
                {expensesFailed ? (
                  /* A refused or dropped read is not "no expenses". */
                  <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                    <LoadFailureNotice error={queryFailureError(expensesQuery)} subject="fleet expenses" allowed="admins, office staff, supervisors and dispatchers" />
                  </div>
                ) : expensesQuery.isLoading ? (
                  <div className="h-16 animate-pulse rounded-fi-panel bg-fieldiq-card" aria-hidden="true" />
                ) : expenses.length === 0 ? (
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-8 text-center space-y-2">
                    <FileText className="w-8 h-8 text-fieldiq-muted mx-auto" />
                    <p className="text-sm font-semibold text-fieldiq-text">No expenses yet</p>
                    <p className="text-xs text-fieldiq-muted">Upload a receipt or enter one manually to get started.</p>
                  </div>
                ) : (
                  expenses.map(exp => (
                    <div key={exp.id} className="bg-fieldiq-card border border-fieldiq-border rounded-fi-panel p-3 flex items-center gap-3">
                      <span className="text-[11px] text-fieldiq-muted w-16 shrink-0 font-medium">
                        {formatCalendarDate(exp.date, { month: 'short', day: 'numeric' }) ?? '—'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-fieldiq-text truncate">{exp.description || '—'}</p>
                        <p className="text-[10px] text-fieldiq-muted truncate">{exp.fleetUnitNumber || '—'}</p>
                      </div>
                      <span className="text-sm font-bold text-fieldiq-text shrink-0">{fmtCurrency(exp.amount)}</span>
                      <span className={`shrink-0 rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink ${categoryOf(exp.category).fill}`}>
                        {categoryOf(exp.category).label}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
